import { useCallback, useState } from 'react';
import clsx from 'clsx';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Select, TagsInput, UnstyledButton } from '@mantine/core';
// components
import { DateRangeSelect, DateRangeKey } from '@newo/ui-kit';
import { IConversationsTurnType } from '@newo/api/gateway';
import ConnectorSelect from '@/modules/Integrations/components/ConnectorSelect';
import EditableDateTimePicker from '@/components/EditableDateTimePicker';
import UUIDInput from '@/components/UUIDInput';
// utils
import { getDateRange } from '@/modules/DateTime/utils';
import useConversationStore from '@/pages/Conversations/Conversations.store';
import {
    useConversationsConnectorsQuery,
    usePersonasQuery
} from '@/services/bff/bff.queries.ts';
import type {
    TActsFilters,
    TUserPersonaFilters
} from '@/pages/Conversations/Conversations.types';
// assets
import styles from '@/pages/Conversations/components/Filters/Filters.module.css';

interface FiltersProps {
    className?: string;
}

const Filters = ({ className }: FiltersProps) => {
    const [dateRange, setDateRange] = useState<DateRangeKey | undefined>();

    const { actsFilters, setActsFilters, setUserPersonaFilters, clearFilters } =
        useConversationStore(
            useShallow((state) => ({
                actsFilters: state.actsFilters,
                setActsFilters: state.setActsFilters,
                setUserPersonaFilters: state.setUserPersonaFilters,
                clearFilters: state.clearFilters
            }))
        );

    const { data } = usePersonasQuery({
        page: 1,
        per: 100,
        is_linked_to_agent: true
    });
    const { data: integrations = [] } = useConversationsConnectorsQuery();

    const agents = data?.items ?? [];

    const handleClearFilters = () => {
        clearFilters();
        setDateRange(undefined);
    };

    const handleTypeChange = (value: string | null) => {
        const filters: TActsFilters | TUserPersonaFilters = {
            turn_type:
                value === 'all' ? undefined : (value as IConversationsTurnType),
            event_idns: value === 'custom' ? actsFilters.event_idns : undefined
        };
        setActsFilters(filters);
        setUserPersonaFilters(filters);
    };

    const handleEventIdnsChange = (eventIdns: string[]) => {
        setActsFilters({ event_idns: eventIdns });
        setUserPersonaFilters({ event_idns: eventIdns });
    };

    const handleSessionIdChange = useCallback(
        (sessionId: string) => {
            setActsFilters({ session_id: sessionId || undefined });
            setUserPersonaFilters({ session_id: sessionId || undefined });
        },
        [setActsFilters, setUserPersonaFilters]
    );

    const handleAgentChange = (agentId?: string | null) => {
        setActsFilters({ agent_persona_id: agentId ?? undefined });
        setUserPersonaFilters({ agent_persona_id: agentId ?? undefined });
    };

    const handleConnectorsChange = (connectors: string[]) => {
        setActsFilters({ connectors });
        setUserPersonaFilters({ connectors });
    };

    const handleFromDateTimeChange = (fromDatetime?: string) => {
        setActsFilters({ from_datetime: fromDatetime });
        setUserPersonaFilters({ from_datetime: fromDatetime });
    };

    const handleToDateTimeChange = (toDatetime?: string) => {
        setActsFilters({ to_datetime: toDatetime });
        setUserPersonaFilters({ to_datetime: toDatetime });
    };

    const handleDateRangeChange = (interval: DateRangeKey | null) => {
        setDateRange(interval ?? undefined);
        const range = getDateRange(interval);
        handleFromDateTimeChange(range.from_datetime);
        handleToDateTimeChange(range.to_datetime);
    };

    return (
        <div className={clsx(styles.filters, className)}>
            <div className="flex items-center justify-between">
                <span className="font-semibold">Filters</span>
                <UnstyledButton
                    className="text-xs text-blue-6"
                    onClick={handleClearFilters}
                >
                    Clear FIlters
                </UnstyledButton>
            </div>
            <Select
                data={[
                    {
                        label: 'All',
                        value: 'all'
                    },
                    {
                        label: 'Messages',
                        value: 'messages'
                    },
                    {
                        label: 'Events',
                        value: 'events'
                    },
                    {
                        label: 'Custom',
                        value: 'custom'
                    }
                ]}
                label="Type"
                placeholder="Select type"
                value={actsFilters.turn_type ?? 'all'}
                onChange={handleTypeChange}
            />
            <TagsInput
                disabled={actsFilters.turn_type !== 'custom'}
                label="Event type"
                placeholder="Type and press Enter to add"
                value={actsFilters.event_idns ?? []}
                onChange={handleEventIdnsChange}
            />
            <UUIDInput
                label="Session Id"
                placeholder="Paste valid session Id"
                value={actsFilters.session_id ?? ''}
                onChange={handleSessionIdChange}
            />
            <Select
                clearable
                data={agents.map((agent) => ({
                    label: agent.name,
                    value: agent.id
                }))}
                label="Agent Personas"
                placeholder="Select persona"
                value={actsFilters.agent_persona_id ?? undefined}
                onChange={handleAgentChange}
                onClear={() => {
                    handleAgentChange();
                }}
            />
            <ConnectorSelect
                integrations={integrations}
                value={actsFilters.connectors ?? []}
                onChange={handleConnectorsChange}
            />

            <DateRangeSelect
                size="xs"
                value={dateRange}
                onChange={handleDateRangeChange}
            />

            <EditableDateTimePicker
                label="From Date & Time"
                placeholder="From Date & Time"
                value={actsFilters.from_datetime}
                onChange={handleFromDateTimeChange}
            />
            <EditableDateTimePicker
                label="To Date & Time"
                placeholder="To Date & Time"
                value={actsFilters.to_datetime}
                onChange={handleToDateTimeChange}
            />
        </div>
    );
};

export default Filters;
